import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PreseasonTimeControllerService, SeasonDefinitionControllerService, TeamControllerService, DictionaryControllerService } from '../api';
import { selectUserId, selectTeamIds, selectTeams, selectTeamType, selectYearOfBirth, selectSportsClubId, updateTeamPreseasonDefined } from '../store/userSlice';
import MapPickerModal from './MapPickerModal';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'react-toastify';
import './Settings.css';

function PushNotificationSettings() {
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications();
  const [loading, setLoading] = useState(false);

  if (!isSupported) {
    return null;
  }

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (isSubscribed) {
        await unsubscribe();
      } else {
        await subscribe();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <h2 className="section-title">Powiadomienia push</h2>
      <div className="push-notification-row">
        <div className="push-notification-info">
          <p className="push-notification-status">
            {isSubscribed ? 'Powiadomienia push aktywne' : 'Powiadomienia push nieaktywne'}
          </p>
          <p className="push-notification-description">
            {permission === 'denied'
              ? 'Powiadomienia zablokowane w ustawieniach przeglądarki'
              : 'Otrzymuj powiadomienia o sparingach nawet gdy nie masz otwartej strony (wyłączenie funkcjonalności spowoduje brak możliwości śledzenia działania aplikacji)'}
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={loading || permission === 'denied'}
          className={`btn push-notification-btn ${isSubscribed ? 'btn-outline' : 'btn-primary'}`}
        >
          {loading ? 'Zapisywanie...' : isSubscribed ? 'Wyłącz' : 'Włącz'}
        </button>
      </div>
    </div>
  );
}

function Settings() {
  const { hasRole } = useAuth();
  const dispatch = useDispatch();
  const isCoach = hasRole('COACH');
  const isSportsClub = hasRole('SPORTS_CLUB');
  const userId = useSelector(selectUserId);
  const sportsClubId = useSelector(selectSportsClubId);
  const teamIds = useSelector(selectTeamIds);
  const coachTeamsFromRedux = useSelector(selectTeams); // Pełne obiekty drużyn coacha z id i teamName
  const teamType = useSelector(selectTeamType);
  const yearOfBirth = useSelector(selectYearOfBirth);
  const coachTeamId = teamIds?.[0]; // Coach ma tylko jeden teamId w liście (dla kompatybilności)

  // Stan dla aktualnie wybranej drużyny w nawigatorze (dla coachów z wieloma drużynami)
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);

  // Funkcja pomocnicza do wyodrębnienia roku z yearOfBirth (np. 'YEAR_2007' -> 2007)
  const getYearFromYearOfBirth = (yob) => {
    if (!yob) return null;
    const match = yob.match(/YEAR_(\d{4})/);
    return match ? parseInt(match[1]) : null;
  };

  // Oblicz czy trener juniorski ma mniej niż 16 lat
  const currentYear = new Date().getFullYear();
  const coachBirthYear = getYearFromYearOfBirth(yearOfBirth);
  const coachAge = coachBirthYear ? currentYear - coachBirthYear : null;
  const isUnder16 = coachAge !== null && coachAge < 16;
  const location = useLocation();
  const navigate = useNavigate();

  const [preseasonTimes, setPreseasonTimes] = useState([]);
  const [selectedPreseasonTime, setSelectedPreseasonTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stany dla drużyn (tylko sportsclub)
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedTeamDefinition, setSelectedTeamDefinition] = useState(null);
  const [teamDefinitionLoading, setTeamDefinitionLoading] = useState(false);

  // Stan dla definicji okresu coacha
  const [coachTeamDefinition, setCoachTeamDefinition] = useState(null);
  const [coachTeamDefinitionLoading, setCoachTeamDefinitionLoading] = useState(false);

  // Stany dla formularza definiowania okresów przygotowawczych (coach)
  const preseasonTimesFromState = location.state?.preseasonTimesToDefine || [];
  const preseasonTimesToDefine = preseasonTimesFromState.length > 0
    ? preseasonTimesFromState
    : JSON.parse(sessionStorage.getItem('preseasonTimesToDefine') || '[]');

  // Czy coach musi zdefiniować okresy przygotowawcze
  const needsPreseasonSetup = isCoach && coachTeamsFromRedux?.length > 0 && !coachTeamsFromRedux.some(t => t.isPreseasonDefined);
  const [preseasonDefinitions, setPreseasonDefinitions] = useState({});
  const [savingDefinitions, setSavingDefinitions] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Stany dla słowników i sparingów
  const [seniorTiers, setSeniorTiers] = useState([]);
  const [youthTiers, setYouthTiers] = useState([]);
  const [yearOfBirths, setYearOfBirths] = useState([]);
  const [sparingSchedules, setSparingSchedules] = useState({});
  const [blockRemainingDates, setBlockRemainingDates] = useState({});
  const [currentSparingIndexes, setCurrentSparingIndexes] = useState({}); // Index dla każdego preseasonTimeId
  const [dateValidationErrors, setDateValidationErrors] = useState({}); // Błędy walidacji dat
  const [sparingValidationErrors, setSparingValidationErrors] = useState({}); // Błędy walidacji dat sparingów

  // Stany dla preferencji geograficznych
  const [territoryDictionary, setTerritoryDictionary] = useState([]); // [{key: 'LODZKIE', displayValue: 'łódzkie', subValues: [{key: 'LODZ', displayValue: 'Łódź'}, ...]}]

  // Stan dla wyboru typu drużyny przeciwnika w sparingach (tylko dla trenerów SENIOR)
  const [sparingOpponentTypes, setSparingOpponentTypes] = useState({}); // { [`${defKey}-${sparingIndex}`]: { senior: true, junior: false } }

  // Stan dla modala definiowania przeciwnika
  const [opponentModalOpen, setOpponentModalOpen] = useState(null); // { preseasonTimeId, sparingIndex } lub null
  const [territoryModalOpen, setTerritoryModalOpen] = useState(null); // definitionKey lub null
  const [territoryDistanceLoading, setTerritoryDistanceLoading] = useState(false);
  const [saveValidationErrors, setSaveValidationErrors] = useState([]); // Lista błędów walidacji wyświetlana nad przyciskiem
  const [campMapPickerOpen, setCampMapPickerOpen] = useState(null); // { defKey, sparingIndex } lub null
  const [campTerritories, setCampTerritories] = useState({}); // { [`${defKey}-${sparingIndex}`]: { voivodeships: [], counties: [], loading: false } }

  // Flaga określająca czy dane z Redux są gotowe
  const isCoachDataReady = isCoach && userId && teamType && coachTeamId;
  const isSportsClubDataReady = isSportsClub && userId;

  // Czyść błędy walidacji gdy formularz się zmienia
  useEffect(() => {
    if (saveValidationErrors.length > 0) {
      setSaveValidationErrors([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preseasonDefinitions, sparingSchedules, dateValidationErrors, sparingValidationErrors]);

  useEffect(() => {
    // Pobierz okresy rozgrywkowe dla wszystkich użytkowników
    if (isCoach || isSportsClub) {
      fetchPreseasonTimes();
      fetchTerritoryDictionary();
    }
  }, [isCoach, isSportsClub]);

  const fetchTerritoryDictionary = async () => {
    try {
      const data = await DictionaryControllerService.getDictionaries();
      setTerritoryDictionary(data.territories || []);
    } catch (error) {
      // error handled silently
    }
  };

  useEffect(() => {
    // Pobieranie słowników - czekaj aż dane z Redux będą gotowe
    if (isCoach) {
      // Dla coacha: czekaj na userId, teamType i coachTeamId
      if (!isCoachDataReady) {
        return;
      }

      if (teamType === 'YOUTH') {
        // Dla trenerów juniorskich: pobieraj z getYouthTeamsSearchDictionaries
        fetchYouthTiersForJuniorCoach();
      } else if (teamType === 'SENIOR') {
        // Dla trenerów seniorskich: pobieraj z getSeniorTeamsSearchDictionaries
        fetchSeniorTiers();
      }
    } else if (isSportsClub) {
      // Dla klubu sportowego: czekaj na userId
      if (!isSportsClubDataReady) {
        return;
      }

      fetchSeniorTiers();
    }
  }, [isCoachDataReady, isSportsClubDataReady, teamType]);

  // Pobierz słownik tierów
  const fetchSeniorTiers = async () => {
    try {
      const data = await DictionaryControllerService.getSeniorTeamsSearchDictionaries();
      setSeniorTiers(data.tiers || []);
      setYearOfBirths(data.yearOfBirths || []);
    } catch (error) {
      // error handled silently
    }
  };

  // Pobierz słownik tierów dla juniorów
  const fetchYouthTiers = async () => {
    if (!coachTeamId) return;

    try {
      const data = await DictionaryControllerService.getYouthTeamsSearchDictionaries(coachTeamId);
      setYouthTiers(data.tiers || []);
      // yearOfBirths pobieramy z getSeniorTeamsSearchDictionaries, nie nadpisujemy tutaj
    } catch (error) {
      // error handled silently
    }
  };

  // Pobierz słownik tierów dla trenerów juniorskich (teamType === 'YOUTH')
  const fetchYouthTiersForJuniorCoach = async () => {
    if (!coachTeamId) return;
    try {
      const data = await DictionaryControllerService.getYouthTeamsSearchDictionaries(coachTeamId);
      setYouthTiers(data.tiers || []);
      setYearOfBirths(data.yearOfBirths || []);
      // Również pobierz seniorTiers jeśli trener ma >= 16 lat
      if (!isUnder16) {
        const seniorData = await DictionaryControllerService.getSeniorTeamsSearchDictionaries();
        setSeniorTiers(seniorData.tiers || []);
      }
    } catch (error) {
      // error handled silently
    }
  };

  // Inicjalizacja formularza dla okresów do zdefiniowania (coach)
  // SeasonDefinitionToDefineDto ma teamIds (tablica), więc rozwijamy na wpisy per drużyna
  useEffect(() => {
    if (preseasonTimesToDefine.length > 0) {
      const initialDefinitions = {};
      preseasonTimesToDefine.forEach(pt => {
        const teams = pt.teamIds || [];
        teams.forEach(teamId => {
          const defKey = `${pt.seasonDefinitionId}-${teamId}`;
          initialDefinitions[defKey] = {
            definitionKey: defKey,
            seasonDefinitionId: pt.seasonDefinitionId,
            teamId: teamId,
            seasonName: pt.seasonName,
            minStartDate: pt.minStartDate,
            maxEndDate: pt.maxEndDate,
            startDate: '',
            endDate: '',
            amountOfGames: ''
          };
        });
      });
      setPreseasonDefinitions(initialDefinitions);
    }
  }, [teamType, isUnder16]);

  // Pobierz słowniki juniorskie gdy trener SENIOR wybierze opcję "Juniorzy"
  useEffect(() => {
    if (isCoach && teamType === 'SENIOR' && youthTiers.length === 0) {
      // Sprawdź czy którykolwiek okres ma zaznaczone "Juniorzy"
      const hasJuniorSelected = Object.values(sparingOpponentTypes).some(ot => ot.junior);
      if (hasJuniorSelected) {
        fetchYouthTiers();
      }
    }
  }, [isCoach, teamType, sparingOpponentTypes, coachTeamId]);

  // Pobierz drużyny dla sportsclub
  useEffect(() => {
    const fetchTeams = async () => {
      if (!isSportsClub || !sportsClubId) return;

      try {
        const teamsData = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
        setTeams(teamsData || []);
      } catch (error) {
        // error handled silently
      }
    };

    fetchTeams();
  }, [isSportsClub, sportsClubId]);

  // Pobierz team definition gdy wybrano okres rozgrywkowy i drużynę (sportsclub)
  useEffect(() => {
    const fetchTeamDefinition = async () => {
      if (!isSportsClub || !selectedPreseasonTime?.id || !selectedTeamId) {
        setSelectedTeamDefinition(null);
        return;
      }

      setTeamDefinitionLoading(true);
      try {
        const definition = await PreseasonTimeControllerService.getTeamDefinitionByTeamIdAndPreseasonTimeId(
          parseInt(selectedTeamId),
          selectedPreseasonTime.id
        );
        setSelectedTeamDefinition(definition || null);
      } catch (error) {
        setSelectedTeamDefinition(null);
      } finally {
        setTeamDefinitionLoading(false);
      }
    };

    fetchTeamDefinition();
  }, [isSportsClub, selectedPreseasonTime, selectedTeamId]);

  // Pobierz team definition dla coacha gdy wybrano okres rozgrywkowy
  useEffect(() => {
    const fetchCoachTeamDefinition = async () => {
      if (!isCoach || !selectedPreseasonTime?.id || !coachTeamId) {
        setCoachTeamDefinition(null);
        return;
      }

      setCoachTeamDefinitionLoading(true);
      try {
        const definitions = await PreseasonTimeControllerService.getTeamDefinitionsByPreseasonTimeId(
          selectedPreseasonTime.id
        );
        // Znajdź definicję dla drużyny coacha
        const coachDefinition = definitions?.find(def => def.teamId === coachTeamId);
        setCoachTeamDefinition(coachDefinition || null);
      } catch (error) {
        setCoachTeamDefinition(null);
      } finally {
        setCoachTeamDefinitionLoading(false);
      }
    };

    fetchCoachTeamDefinition();
  }, [isCoach, selectedPreseasonTime, coachTeamId]);

  const fetchPreseasonTimes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SeasonDefinitionControllerService.getAllSeasonDefinitions();
      setPreseasonTimes(data);
    } catch (err) {
      setError('Nie udalo sie pobrac okresow rozgrywkowych');
    } finally {
      setLoading(false);
    }
  };

  const handlePreseasonTimeChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId === '') {
      setSelectedPreseasonTime(null);
    } else {
      const selected = preseasonTimes.find(pt => pt.id === parseInt(selectedId));
      setSelectedPreseasonTime(selected);
    }
  };

  const handleTeamChange = (e) => {
    setSelectedTeamId(e.target.value);
  };

  // Walidacja dat dla konkretnej definicji
  const validateDates = (definition, field, newValue) => {
    const errors = [];

    const startDate = field === 'startDate' ? newValue : definition.startDate;
    const endDate = field === 'endDate' ? newValue : definition.endDate;
    const minStartDate = definition.minStartDate;
    const maxEndDate = definition.maxEndDate;

    if (startDate && minStartDate && maxEndDate) {
      const start = new Date(startDate);
      const minStart = new Date(minStartDate);
      const maxEnd = new Date(maxEndDate);

      if (start < minStart) {
        errors.push('Data rozpoczęcia przygotowań nie może być wcześniejsza niż początek okresu');
      }
      if (start > maxEnd) {
        errors.push('Data rozpoczęcia przygotowań nie może być późniejsza niż koniec okresu');
      }
    }

    if (endDate && minStartDate && maxEndDate) {
      const end = new Date(endDate);
      const minStart = new Date(minStartDate);
      const maxEnd = new Date(maxEndDate);

      if (end < minStart) {
        errors.push('Data zakończenia przygotowań nie może być wcześniejsza niż początek okresu');
      }
      if (end > maxEnd) {
        errors.push('Data zakończenia przygotowań nie może być późniejsza niż koniec okresu');
      }
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start >= end) {
        errors.push('Data rozpoczęcia przygotowań musi być wcześniejsza niż data zakończenia');
      }
    }

    return errors;
  };

  // Walidacja dat sparingu względem dat przygotowań
  const validateSparingDates = (preseasonTimeId, sparingIndex, sparing, definition, allSparings) => {
    const errors = [];

    if (!definition || !definition.startDate || !definition.endDate) {
      return errors;
    }

    const prepStart = definition.startDate;
    const prepEnd = definition.endDate;

    if (sparing.startDate) {
      if (sparing.startDate < prepStart) {
        errors.push('Data od nie może być wcześniejsza niż data rozpoczęcia przygotowań');
      }
      if (sparing.startDate > prepEnd) {
        errors.push('Data od nie może być późniejsza niż data zakończenia przygotowań');
      }
    }

    if (sparing.endDate) {
      if (sparing.endDate < prepStart) {
        errors.push('Data do nie może być wcześniejsza niż data rozpoczęcia przygotowań');
      }
      if (sparing.endDate > prepEnd) {
        errors.push('Data do nie może być późniejsza niż data zakończenia przygotowań');
      }
    }

    if (sparing.startDate && sparing.endDate) {
      if (sparing.startDate > sparing.endDate) {
        errors.push('Data od musi być wcześniejsza lub równa dacie do');
      }
    }

    // Sprawdź nakładanie się z innymi sparingami
    if (sparing.startDate && sparing.endDate && allSparings) {
      allSparings.forEach((otherSparing, otherIndex) => {
        if (otherIndex !== sparingIndex && otherSparing.startDate && otherSparing.endDate) {
          // Zakresy się nakładają gdy: currentStart <= otherEnd && currentEnd >= otherStart
          if (sparing.startDate <= otherSparing.endDate && sparing.endDate >= otherSparing.startDate) {
            errors.push(`Zakres dat nakłada się ze sparingiem ${otherIndex + 1}`);
          }
        }
      });
    }

    return errors;
  };

  // Rewalidacja wszystkich sparingów dla danej definicji
  const revalidateAllSparings = (preseasonTimeId, definition, updatedSchedules = null) => {
    const schedules = updatedSchedules || sparingSchedules[preseasonTimeId] || [];
    const newErrors = {};

    schedules.forEach((sparing, index) => {
      const errors = validateSparingDates(preseasonTimeId, index, sparing, definition, schedules);
      if (errors.length > 0) {
        newErrors[`${preseasonTimeId}-${index}`] = errors;
      }
    });

    setSparingValidationErrors(prev => {
      // Usuń stare błędy dla tej definicji
      const filtered = Object.keys(prev)
        .filter(key => !key.startsWith(`${preseasonTimeId}-`))
        .reduce((obj, key) => ({ ...obj, [key]: prev[key] }), {});

      return { ...filtered, ...newErrors };
    });
  };

  // Obsługa zmiany pól formularza definiowania okresu przygotowawczego
  const handleDefinitionChange = (defKey, field, value) => {
    setPreseasonDefinitions(prev => {
      const updatedDefinition = {
        ...prev[defKey],
        [field]: value
      };

      // Waliduj daty po aktualizacji
      if (field === 'startDate' || field === 'endDate') {
        const errors = validateDates(prev[defKey], field, value);
        setDateValidationErrors(prevErrors => ({
          ...prevErrors,
          [defKey]: errors
        }));

        // Rewaliduj sparingi gdy zmienią się daty przygotowań
        setTimeout(() => {
          revalidateAllSparings(defKey, updatedDefinition);
        }, 0);
      }

      return {
        ...prev,
        [defKey]: updatedDefinition
      };
    });

  };

  // Synchronizuj kafelki sparingów po utracie focusa pola amountOfGames
  const handleAmountOfGamesBlur = (defKey) => {
    const definition = preseasonDefinitions[defKey];
    if (!definition) return;

    const count = parseInt(definition.amountOfGames) || 0;
    setSparingSchedules(prev => {
      const currentSchedules = prev[defKey] || [];
      const newSchedules = [];

      for (let i = 0; i < count; i++) {
        newSchedules.push(currentSchedules[i] || {
          startDate: '',
          endDate: '',
          gamesCount: 1,
          opponentTiers: [],
          opponentYearOfBirths: [],
          opponentYouthTiers: [],
          isAtCamp: false,
          campLatitude: null,
          campLongitude: null,
          campAddress: '',
          campMaxDistanceKm: ''
        });
      }

      return {
        ...prev,
        [defKey]: newSchedules
      };
    });

    setCurrentSparingIndexes(prev => ({
      ...prev,
      [defKey]: 0
    }));
  };

  // Usuń sparing i zmniejsz liczbę sparingów
  const handleRemoveSparing = (defKey, sparingIndex) => {
    setSparingSchedules(prev => {
      const updated = (prev[defKey] || []).filter((_, i) => i !== sparingIndex);
      return { ...prev, [defKey]: updated };
    });

    setPreseasonDefinitions(prev => {
      const currentCount = parseInt(prev[defKey]?.amountOfGames) || 0;
      return {
        ...prev,
        [defKey]: {
          ...prev[defKey],
          amountOfGames: String(Math.max(0, currentCount - 1))
        }
      };
    });

    // Remap sparingOpponentTypes keys after removing a sparing
    setSparingOpponentTypes(prev => {
      const remapped = { ...prev };
      const scheduleLength = sparingSchedules[defKey]?.length || 0;
      // Delete the removed index key
      delete remapped[`${defKey}-${sparingIndex}`];
      // Shift all keys after the removed index down by 1
      for (let i = sparingIndex + 1; i < scheduleLength; i++) {
        const oldKey = `${defKey}-${i}`;
        const newKey = `${defKey}-${i - 1}`;
        if (remapped[oldKey] !== undefined) {
          remapped[newKey] = remapped[oldKey];
          delete remapped[oldKey];
        }
      }
      return remapped;
    });

    setCurrentSparingIndexes(prev => {
      const maxIndex = Math.max(0, (sparingSchedules[defKey]?.length || 1) - 2);
      return {
        ...prev,
        [defKey]: Math.min(prev[defKey] || 0, maxIndex)
      };
    });
  };

  // Wstaw nowy sparing pomiędzy istniejące
  const handleInsertSparing = (defKey, insertAfterIndex) => {
    setSparingSchedules(prev => {
      const schedules = prev[defKey] || [];
      const prevSparing = schedules[insertAfterIndex];
      const nextSparing = schedules[insertAfterIndex + 1];

      // Sprawdź czy oba sąsiednie sparingi mają daty weekendowe (pt-nd)
      const isWeekend = (sparing) => {
        if (!sparing?.startDate || !sparing?.endDate) return false;
        const startDay = new Date(sparing.startDate).getDay();
        return startDay === 5 || startDay === 6 || startDay === 0; // pt, sob, nd
      };

      const newSparing = {
        startDate: '',
        endDate: '',
        gamesCount: 1,
        opponentTiers: [],
        opponentYearOfBirths: [],
        opponentYouthTiers: [],
        isAtCamp: false,
        campLatitude: null,
        campLongitude: null,
        campAddress: '',
        campMaxDistanceKm: ''
      };

      // Jeśli oba sąsiednie sparingi mają daty weekendowe, uzupełnij środą między nimi
      if (isWeekend(prevSparing) && isWeekend(nextSparing)) {
        const prevEnd = new Date(prevSparing.endDate);
        const nextStart = new Date(nextSparing.startDate);
        // Znajdź środę między nimi
        const candidate = new Date(prevEnd);
        candidate.setDate(candidate.getDate() + 1);
        while (candidate < nextStart) {
          if (candidate.getDay() === 3) { // środa
            const formatDate = (d) => d.toISOString().split('T')[0];
            const wed = formatDate(candidate);
            newSparing.startDate = wed;
            newSparing.endDate = wed;
            break;
          }
          candidate.setDate(candidate.getDate() + 1);
        }
      }

      const updated = [
        ...schedules.slice(0, insertAfterIndex + 1),
        newSparing,
        ...schedules.slice(insertAfterIndex + 1)
      ];
      return { ...prev, [defKey]: updated };
    });

    setPreseasonDefinitions(prev => {
      const currentCount = parseInt(prev[defKey]?.amountOfGames) || 0;
      return {
        ...prev,
        [defKey]: {
          ...prev[defKey],
          amountOfGames: String(currentCount + 1)
        }
      };
    });

    // Remap sparingOpponentTypes keys after inserting a sparing
    setSparingOpponentTypes(prev => {
      const remapped = { ...prev };
      const scheduleLength = sparingSchedules[defKey]?.length || 0;
      // Shift all keys from end down to insertAfterIndex+1, incrementing by 1
      for (let i = scheduleLength - 1; i > insertAfterIndex; i--) {
        const oldKey = `${defKey}-${i}`;
        const newKey = `${defKey}-${i + 1}`;
        if (remapped[oldKey] !== undefined) {
          remapped[newKey] = remapped[oldKey];
          delete remapped[oldKey];
        }
      }
      // Clear the key for the newly inserted sparing
      delete remapped[`${defKey}-${insertAfterIndex + 1}`];
      return remapped;
    });
  };

  // Obsługa zmiany pól sparingu
  const handleSparingChange = (defKey, sparingIndex, field, value) => {
    let sanitizedValue = value;
    if (field === 'gamesCount') {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1) sanitizedValue = 1;
      else if (num > 2) sanitizedValue = 2;
      else sanitizedValue = num;
    }
    setSparingSchedules(prev => {
      const updatedSchedules = prev[defKey].map((sparing, index) => {
        if (index === sparingIndex) {
          return { ...sparing, [field]: sanitizedValue };
        }
        return sparing;
      });

      // Waliduj daty wszystkich sparingów (bo zmiana jednego może wpływać na nakładanie się z innymi)
      if (field === 'startDate' || field === 'endDate') {
        const definition = preseasonDefinitions[defKey];
        // Rewaliduj wszystkie sparingi z przekazaniem zaktualizowanych danych
        setTimeout(() => {
          revalidateAllSparings(defKey, definition, updatedSchedules);
        }, 0);
      }

      return {
        ...prev,
        [defKey]: updatedSchedules
      };
    });
  };

  // Obsługa checkboxa dla klasy rozgrywkowej
  const handleTierCheckboxChange = (defKey, sparingIndex, tierKey, checked) => {
    setSparingSchedules(prev => ({
      ...prev,
      [defKey]: prev[defKey].map((sparing, index) => {
        if (index === sparingIndex) {
          const currentTiers = sparing.opponentTiers || [];
          const newTiers = checked
            ? [...currentTiers, tierKey]
            : currentTiers.filter(t => t !== tierKey);
          return { ...sparing, opponentTiers: newTiers };
        }
        return sparing;
      })
    }));
  };

  // Obsługa checkboxa dla rocznika (juniorzy)
  const handleYearOfBirthCheckboxChange = (defKey, sparingIndex, yearKey, checked) => {
    setSparingSchedules(prev => ({
      ...prev,
      [defKey]: prev[defKey].map((sparing, index) => {
        if (index === sparingIndex) {
          const currentYears = sparing.opponentYearOfBirths || [];
          const newYears = checked
            ? [...currentYears, yearKey]
            : currentYears.filter(y => y !== yearKey);
          return { ...sparing, opponentYearOfBirths: newYears };
        }
        return sparing;
      })
    }));
  };

  // Obsługa checkboxa dla klasy rozgrywkowej juniorów
  const handleYouthTierCheckboxChange = (defKey, sparingIndex, tierKey, checked) => {
    setSparingSchedules(prev => ({
      ...prev,
      [defKey]: prev[defKey].map((sparing, index) => {
        if (index === sparingIndex) {
          const currentTiers = sparing.opponentYouthTiers || [];
          const newTiers = checked
            ? [...currentTiers, tierKey]
            : currentTiers.filter(t => t !== tierKey);
          return { ...sparing, opponentYouthTiers: newTiers };
        }
        return sparing;
      })
    }));
  };

  // Obsługa zmiany typu przeciwnika (senior/junior) - dla konkretnego sparingu
  const handleSparingOpponentTypeChange = (defKey, sparingIndex, type, checked) => {
    const key = `${defKey}-${sparingIndex}`;
    setSparingOpponentTypes(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [type]: checked
      }
    }));
  };

  // Nawigacja między sparingami
  const handlePreviousSparing = (defKey) => {
    setCurrentSparingIndexes(prev => ({
      ...prev,
      [defKey]: Math.max(0, (prev[defKey] || 0) - 1)
    }));
  };

  const handleNextSparing = (defKey) => {
    const maxIndex = (sparingSchedules[defKey]?.length || 1) - 1;
    setCurrentSparingIndexes(prev => ({
      ...prev,
      [defKey]: Math.min(maxIndex, (prev[defKey] || 0) + 1)
    }));
  };

  // Obsługa checkboxa blokowania pozostałych terminów
  const handleBlockRemainingDatesChange = (defKey, checked) => {
    setBlockRemainingDates(prev => ({
      ...prev,
      [defKey]: checked
    }));
  };

  // Obsługa checkboxa województwa
  const handleVoivodeshipToggle = (defKey, voivodeshipKey) => {
    setPreseasonDefinitions(prev => {
      const definition = prev[defKey];
      const current = definition.selectedVoivodeships || [];
      const newVoivodeships = current.includes(voivodeshipKey)
        ? current.filter(v => v !== voivodeshipKey)
        : [...current, voivodeshipKey];

      // Przy usunięciu województwa, usuń też powiaty z tego województwa
      let newCounties = definition.selectedCounties || [];
      if (!newVoivodeships.includes(voivodeshipKey)) {
        const voivodeshipDict = territoryDictionary.find(t => t.key === voivodeshipKey);
        const countyKeys = (voivodeshipDict?.subValues || []).map(c => c.key);
        newCounties = newCounties.filter(c => !countyKeys.includes(c));
      }

      return {
        ...prev,
        [defKey]: { ...definition, selectedVoivodeships: newVoivodeships, selectedCounties: newCounties }
      };
    });
  };

  // Obsługa checkboxa powiatu
  const handleCountyToggle = (defKey, countyKey) => {
    setPreseasonDefinitions(prev => {
      const definition = prev[defKey];
      const current = definition.selectedCounties || [];
      const newCounties = current.includes(countyKey)
        ? current.filter(c => c !== countyKey)
        : [...current, countyKey];
      return {
        ...prev,
        [defKey]: { ...definition, selectedCounties: newCounties }
      };
    });
  };

  // Obsługa camp checkboxa
  const handleCampToggle = (defKey, sparingIndex) => {
    setSparingSchedules(prev => ({
      ...prev,
      [defKey]: prev[defKey].map((sparing, index) => {
        if (index === sparingIndex) {
          const newIsAtCamp = !sparing.isAtCamp;
          if (!newIsAtCamp) {
            setCampTerritories(prev => ({ ...prev, [`${defKey}-${sparingIndex}`]: undefined }));
            return {
              ...sparing,
              isAtCamp: false,
              campAddress: '',
              campLatitude: null,
              campLongitude: null,
              campMaxDistanceKm: ''
            };
          }
          return { ...sparing, isAtCamp: true };
        }
        return sparing;
      })
    }));
  };

  // Obsługa camp pól
  const handleCampFieldChange = (defKey, sparingIndex, field, value) => {
    setSparingSchedules(prev => {
      const updated = {
        ...prev,
        [defKey]: prev[defKey].map((sparing, index) => {
          if (index === sparingIndex) {
            return { ...sparing, [field]: value };
          }
          return sparing;
        })
      };
      // Auto-fetch camp territories when distance changes
      if (field === 'campMaxDistanceKm') {
        const sparing = updated[defKey][sparingIndex];
        if (sparing.campLatitude != null && sparing.campLongitude != null && value) {
          fetchCampTerritories(defKey, sparingIndex, sparing.campLatitude, sparing.campLongitude, parseInt(value));
        } else {
          setCampTerritories(prev => ({ ...prev, [`${defKey}-${sparingIndex}`]: undefined }));
        }
      }
      return updated;
    });
  };

  // Obsługa zatwierdzenia lokalizacji obozu z mapy
  const handleCampMapConfirm = (result) => {
    if (!campMapPickerOpen) return;
    const { defKey, sparingIndex } = campMapPickerOpen;
    setSparingSchedules(prev => ({
      ...prev,
      [defKey]: prev[defKey].map((sparing, index) => {
        if (index === sparingIndex) {
          return {
            ...sparing,
            campLatitude: result.latitude,
            campLongitude: result.longitude,
            campAddress: result.searchTerm || result.displayName
          };
        }
        return sparing;
      })
    }));
    setCampMapPickerOpen(null);

    // Auto-fetch camp territories after setting location
    const sparing = sparingSchedules[defKey]?.[sparingIndex];
    if (sparing?.campMaxDistanceKm && result.latitude != null && result.longitude != null) {
      fetchCampTerritories(defKey, sparingIndex, result.latitude, result.longitude, parseInt(sparing.campMaxDistanceKm));
    }
  };

  // Pobierz powiaty w zasięgu obozu (informacyjnie)
  const fetchCampTerritories = async (defKey, sparingIndex, lat, lon, distanceKm) => {
    const key = `${defKey}-${sparingIndex}`;
    if (!lat || !lon || !distanceKm) {
      setCampTerritories(prev => ({ ...prev, [key]: undefined }));
      return;
    }
    setCampTerritories(prev => ({ ...prev, [key]: { voivodeships: [], counties: [], loading: true } }));
    try {
      const result = await DictionaryControllerService.getTerritoriesByCoordinates(lat, lon, distanceKm);
      const countyNames = (result.counties || []).map(countyKey => {
        for (const voiv of territoryDictionary) {
          const found = (voiv.subValues || []).find(c => c.key === countyKey);
          if (found) return found.displayValue;
        }
        return countyKey;
      });
      setCampTerritories(prev => ({
        ...prev,
        [key]: { voivodeships: result.voivodeships || [], counties: result.counties || [], countyNames, loading: false }
      }));
    } catch (err) {
      console.error('Error fetching camp territories:', err);
      setCampTerritories(prev => ({ ...prev, [key]: undefined }));
    }
  };

  // Pobierz powiaty dla wybranych województw
  const getFilteredCounties = (selectedVoivodeships) => {
    if (!selectedVoivodeships || selectedVoivodeships.length === 0) return [];
    return territoryDictionary
      .filter(t => selectedVoivodeships.includes(t.key))
      .flatMap(t => (t.subValues || []).map(c => ({ ...c, voivodeship: t.key, voivodeshipName: t.displayValue })));
  };

  // Uzupełnij weekendami — wyznacz weekendy (pt-nd) między datami i wypełnij sparingi
  const handleFillWithWeekends = (defKey) => {
    const definition = preseasonDefinitions[defKey];
    if (!definition) return;

    if (!definition.startDate || !definition.endDate) {
      alert('Najpierw ustaw datę rozpoczęcia i zakończenia przygotowań.');
      return;
    }

    const start = new Date(definition.startDate);
    const end = new Date(definition.endDate);
    if (start >= end) {
      alert('Data rozpoczęcia musi być wcześniejsza niż data zakończenia.');
      return;
    }

    // Znajdź wszystkie weekendy (piątek-niedziela) w zakresie dat
    const weekends = [];
    const current = new Date(start);

    while (current <= end) {
      const dayOfWeek = current.getDay(); // 0=Sun, 5=Fri, 6=Sat
      if (dayOfWeek === 5) { // piątek
        const friday = new Date(current);
        const sunday = new Date(current);
        sunday.setDate(sunday.getDate() + 2);

        // Ogranicz niedzielę do daty końcowej
        const weekendEnd = sunday <= end ? sunday : new Date(end);

        const formatDate = (d) => d.toISOString().split('T')[0];
        weekends.push({
          startDate: formatDate(friday),
          endDate: formatDate(weekendEnd),
          gamesCount: 1,
          opponentTiers: [],
          opponentYearOfBirths: [],
          opponentYouthTiers: [],
          isAtCamp: false,
          campLatitude: null,
          campLongitude: null,
          campAddress: '',
          campMaxDistanceKm: ''
        });

        // Przeskocz do poniedziałku
        current.setDate(current.getDate() + (7 - dayOfWeek));
        continue;
      }
      current.setDate(current.getDate() + 1);
    }

    if (weekends.length === 0) {
      alert('Brak weekendów w wybranym zakresie dat.');
      return;
    }

    // Ustaw liczbę sparingów
    setPreseasonDefinitions(prev => ({
      ...prev,
      [defKey]: {
        ...prev[defKey],
        amountOfGames: String(weekends.length)
      }
    }));

    // Wypełnij harmonogram sparingów
    setSparingSchedules(prev => ({
      ...prev,
      [defKey]: weekends
    }));

    setCurrentSparingIndexes(prev => ({
      ...prev,
      [defKey]: 0
    }));
  };

  // Ustal terytoria na podstawie odległości
  const handleTerritoryByDistance = async (defKey) => {
    const definition = preseasonDefinitions[defKey];
    if (!definition) return;

    const distance = definition.maxSearchDistanceKm;
    if (!distance) {
      alert('Najpierw ustaw maksymalną odległość wyszukiwania.');
      return;
    }

    const teamId = definition.teamId;
    if (!teamId) {
      alert('Brak przypisanej drużyny.');
      return;
    }

    setTerritoryDistanceLoading(true);
    try {
      const result = await DictionaryControllerService.getTerritoriesByDistance(teamId, parseInt(distance));
      setPreseasonDefinitions(prev => ({
        ...prev,
        [defKey]: {
          ...prev[defKey],
          selectedVoivodeships: result.voivodeships || [],
          selectedCounties: result.counties || []
        }
      }));
    } catch (err) {
      console.error('Error fetching territories by distance:', err);
      alert('Nie udało się ustalić terytoriów. Sprawdź czy klub ma ustawione współrzędne geograficzne.');
    } finally {
      setTerritoryDistanceLoading(false);
    }
  };

  // Sprawdź czy są błędy walidacji dat
  const hasDateValidationErrors = () => {
    const hasMainErrors = Object.values(dateValidationErrors).some(errors => errors && errors.length > 0);
    const hasSparingErrors = Object.values(sparingValidationErrors).some(errors => errors && errors.length > 0);
    return hasMainErrors || hasSparingErrors;
  };

  // Sprawdź czy przeciwnik jest zdefiniowany dla sparingu
  const isOpponentDefined = (sparing, defKey, sparingIndex) => {
    const opponentType = sparingOpponentTypes[`${defKey}-${sparingIndex}`];
    const seniorSelected = opponentType?.senior ?? (teamType === 'YOUTH' ? false : true);
    // Junior validation disabled for seniors-only mode
    const juniorSelected = false;

    // Sprawdź czy wybrano przynajmniej jeden typ przeciwnika
    if (!seniorSelected && !juniorSelected) {
      return false;
    }

    // Jeśli wybrano seniorów, sprawdź czy wybrano przynajmniej jeden tier
    if (seniorSelected && (!sparing.opponentTiers || sparing.opponentTiers.length === 0)) {
      return false;
    }

    // Jeśli wybrano juniorów, sprawdź czy wybrano przynajmniej jeden rocznik i tier
    if (juniorSelected) {
      if (!sparing.opponentYearOfBirths || sparing.opponentYearOfBirths.length === 0) {
        return false;
      }
      if (!sparing.opponentYouthTiers || sparing.opponentYouthTiers.length === 0) {
        return false;
      }
    }

    return true;
  };

  // Zbiera błędy walidacji dla podanych definicji
  const getValidationErrors = (definitions) => {
    const errors = [];

    for (const definition of definitions) {
      const defLabel = definition.seasonName || definition.definitionKey;

      if (!definition.startDate || !definition.endDate) {
        errors.push(`${defLabel}: Uzupełnij daty rozpoczęcia i zakończenia`);
        continue;
      }
      if (!definition.amountOfGames) {
        errors.push(`${defLabel}: Uzupełnij liczbę sparingów`);
        continue;
      }
      const startDate = new Date(definition.startDate);
      const endDate = new Date(definition.endDate);
      const minStartDate = new Date(definition.minStartDate);
      const maxEndDate = new Date(definition.maxEndDate);

      if (startDate < minStartDate || startDate > maxEndDate) {
        errors.push(`${defLabel}: Data rozpoczęcia jest poza zakresem okresu`);
      }
      if (endDate < minStartDate || endDate > maxEndDate) {
        errors.push(`${defLabel}: Data zakończenia jest poza zakresem okresu`);
      }
      if (startDate >= endDate) {
        errors.push(`${defLabel}: Data rozpoczęcia musi być wcześniejsza niż data zakończenia`);
      }

      const amountOfGames = parseInt(definition.amountOfGames);
      if (isNaN(amountOfGames) || amountOfGames <= 0) {
        errors.push(`${defLabel}: Nieprawidłowa liczba sparingów`);
        continue;
      }

      const sparings = sparingSchedules[definition.definitionKey] || [];
      if (sparings.length !== amountOfGames) {
        errors.push(`${defLabel}: Zdefiniuj wszystkie sparingi (${sparings.length}/${amountOfGames})`);
        continue;
      }

      const dateErrors = dateValidationErrors[definition.definitionKey];
      if (dateErrors && dateErrors.length > 0) {
        errors.push(`${defLabel}: Popraw błędy dat`);
      }

      for (let i = 0; i < sparings.length; i++) {
        const sparing = sparings[i];
        const sparingLabel = `${defLabel}, sparing ${i + 1}`;

        if (!sparing.startDate || !sparing.endDate) {
          errors.push(`${sparingLabel}: Uzupełnij daty`);
        }

        const gamesCount = parseInt(sparing.gamesCount);
        if (isNaN(gamesCount) || gamesCount <= 0) {
          errors.push(`${sparingLabel}: Uzupełnij liczbę meczów`);
        }

        if (!isOpponentDefined(sparing, definition.definitionKey, i)) {
          errors.push(`${sparingLabel}: Zdefiniuj przeciwnika`);
        }

        if (sparing.isAtCamp) {
          if (sparing.campLatitude == null || sparing.campLongitude == null) {
            errors.push(`${sparingLabel}: Zaznacz lokalizację obozu na mapie`);
          }
          if (!sparing.campMaxDistanceKm || parseInt(sparing.campMaxDistanceKm) <= 0) {
            errors.push(`${sparingLabel}: Wpisz maksymalną odległość z obozu`);
          }
        }

        const sparingErrors = sparingValidationErrors[`${definition.definitionKey}-${i}`];
        if (sparingErrors && sparingErrors.length > 0) {
          errors.push(`${sparingLabel}: Popraw błędy dat sparingu`);
        }
      }
    }

    return errors;
  };

  // Walidacja wszystkich pól formularza
  const validateAllFields = () => {
    return getValidationErrors(Object.values(preseasonDefinitions)).length === 0;
  };

  // Pobierz drużyny coacha z preseasonTimesToDefine (tylko te wymagające definicji)
  const getCoachTeams = () => {
    // Zbierz unikalne teamIds z preseasonTimesToDefine
    const teamIdsFromDefinitions = new Set();
    preseasonTimesToDefine.forEach(pt => {
      (pt.teamIds || []).forEach(id => teamIdsFromDefinitions.add(id));
    });

    // Buduj mapę nazw z Redux (jeśli dostępne)
    const reduxTeamMap = new Map();
    if (coachTeamsFromRedux && coachTeamsFromRedux.length > 0) {
      coachTeamsFromRedux.forEach(team => {
        reduxTeamMap.set(team.id, team.teamName || `Drużyna ${team.id}`);
      });
    }

    // Pokaż WSZYSTKIE drużyny z definicji, uzupełniając nazwami z Redux
    return Array.from(teamIdsFromDefinitions).map(id => ({
      id: id,
      name: reduxTeamMap.get(id) || `Drużyna ${id}`
    }));
  };

  const coachTeamsList = getCoachTeams();
  const hasMultipleTeams = coachTeamsList.length > 1;
  const currentTeam = coachTeamsList[currentTeamIndex] || coachTeamsList[0];

  // Filtruj definicje dla aktualnie wybranej drużyny
  const getDefinitionsForCurrentTeam = () => {
    if (!currentTeam) return [];
    return Object.values(preseasonDefinitions).filter(def => def.teamId === currentTeam.id);
  };

  // Walidacja definicji dla konkretnej drużyny
  const validateTeamDefinitions = (teamId) => {
    const teamDefinitions = Object.values(preseasonDefinitions).filter(def => def.teamId === teamId);
    if (teamDefinitions.length === 0) return false;
    return getValidationErrors(teamDefinitions).length === 0;
  };

  // Błędy walidacji dla konkretnej drużyny (do wyświetlenia w UI)
  const getTeamValidationErrors = (teamId) => {
    const teamDefinitions = Object.values(preseasonDefinitions).filter(def => def.teamId === teamId);
    return getValidationErrors(teamDefinitions);
  };

  // Sprawdź czy aktualna drużyna ma poprawne definicje
  const isCurrentTeamValid = currentTeam ? validateTeamDefinitions(currentTeam.id) : false;

  // Sprawdź czy wszystkie drużyny mają poprawne definicje
  const areAllTeamsValid = coachTeamsList.every(team => validateTeamDefinitions(team.id));

  // Nawigacja do następnej drużyny
  const handleNextTeam = () => {
    if (currentTeamIndex < coachTeamsList.length - 1) {
      setCurrentTeamIndex(currentTeamIndex + 1);
    }
  };

  // Nawigacja do poprzedniej drużyny
  const handlePreviousTeam = () => {
    if (currentTeamIndex > 0) {
      setCurrentTeamIndex(currentTeamIndex - 1);
    }
  };

  // Czy jesteśmy na ostatniej drużynie
  const isLastTeam = currentTeamIndex === coachTeamsList.length - 1;

  // Zapisanie definicji okresów przygotowawczych
  const handleSaveDefinitions = async () => {
    setSavingDefinitions(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const definitionsToSave = Object.values(preseasonDefinitions).map(def => {
        const schedules = sparingSchedules[def.definitionKey]?.map(sparing => {
          // Połącz wszystkie wybrane tiers (seniorskie i juniorskie) do jednej tablicy
          const allTiers = [
            ...(sparing.opponentTiers || []),
            ...(sparing.opponentYouthTiers || [])
          ];

          return {
            tiers: allTiers.length > 0 ? allTiers : undefined,
            yearOfBirths: sparing.opponentYearOfBirths && sparing.opponentYearOfBirths.length > 0 ? sparing.opponentYearOfBirths : undefined,
            startDate: sparing.startDate || undefined,
            endDate: sparing.endDate || undefined,
            amountOfGames: sparing.gamesCount ? parseInt(sparing.gamesCount) : 1,
            isAtCamp: sparing.isAtCamp || false,
            campLatitude: sparing.isAtCamp ? sparing.campLatitude : undefined,
            campLongitude: sparing.isAtCamp ? sparing.campLongitude : undefined,
            campAddress: sparing.isAtCamp ? sparing.campAddress : undefined,
            campMaxDistanceKm: sparing.isAtCamp && sparing.campMaxDistanceKm ? parseInt(sparing.campMaxDistanceKm) : undefined
          };
        }).filter(s => s.tiers || s.yearOfBirths || s.startDate || s.endDate) || [];

        // Build territories from selectedVoivodeships and selectedCounties
        const territories = [];
        const selectedVoivodeships = def.selectedVoivodeships || [];
        const selectedCounties = def.selectedCounties || [];

        selectedVoivodeships.forEach(voiv => {
          // Check if any counties are selected for this voivodeship
          const voivDict = territoryDictionary.find(t => t.key === voiv);
          const voivCountyKeys = (voivDict?.subValues || []).map(c => c.key);
          const selectedCountiesForVoiv = selectedCounties.filter(c => voivCountyKeys.includes(c));

          if (selectedCountiesForVoiv.length > 0 && selectedCountiesForVoiv.length < voivCountyKeys.length) {
            // Some counties selected — send specific counties
            selectedCountiesForVoiv.forEach(county => {
              territories.push({ voivodeship: voiv, county: county });
            });
          } else {
            // No counties selected or ALL counties selected — send whole voivodeship (county = null)
            territories.push({ voivodeship: voiv, county: null });
          }
        });

        return {
          seasonDefinitionId: def.seasonDefinitionId,
          teamId: def.teamId,
          schedules: schedules.length > 0 ? schedules : undefined
        };
      });

      // Save each definition as team schedules
      for (const defToSave of definitionsToSave) {
        await PreseasonTimeControllerService.addTeamSchedules(defToSave);
      }
      setSaveSuccess(true);
      sessionStorage.removeItem('preseasonTimesToDefine');

      // Zaktualizuj Redux - ustaw isPreseasonDefined na true dla wszystkich drużyn trenera
      coachTeamsList.forEach(team => {
        dispatch(updateTeamPreseasonDefined({ teamId: team.id, isPreseasonDefined: true }));
      });

      // Po 2 sekundach przekieruj na stronę z planami
      setTimeout(() => {
        navigate('/preseason-games-plans', { replace: true });
      }, 2000);
    } catch (err) {
      setSaveError('Nie udało się zapisać definicji okresów przygotowawczych');
    } finally {
      setSavingDefinitions(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="settings-container">
      <div className={`settings-card ${isCoach && preseasonTimesToDefine.length > 0 ? 'coach-wide' : ''}`}>
        <div className="settings-header">
          <div className="settings-avatar">
            <span>&#9881;</span>
          </div>
          <h1>Ustawienia</h1>
          <p>Konfiguracja aplikacji</p>
        </div>

        {needsPreseasonSetup && (
          <div className="preseason-required-banner">
            Aby rozpocząć korzystanie z aplikacji musisz zdefiniować okresy przygotowawcze
          </div>
        )}

        <div className="settings-content">
          {/* Formularz definiowania okresów przygotowawczych dla coacha */}
          {isCoach && preseasonTimesToDefine.length > 0 && (
            <div className="settings-section preseason-definition-section">
              <h2 className="section-title">Zdefiniuj okresy przygotowawcze</h2>
              <p className="info-text" style={{ marginBottom: '20px' }}>
                Musisz zdefiniować daty i liczbę sparingów dla poniższych okresów przygotowawczych.
              </p>

              {saveError && <p className="error-text">{saveError}</p>}
              {saveSuccess && <p className="success-text">Definicje zostały zapisane pomyślnie! Przekierowywanie...</p>}

              {/* Nawigator drużyn - wyświetlany gdy coach ma drużyny do zdefiniowania */}
              {coachTeamsList.length > 0 && (
                <div className="team-navigator" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  {hasMultipleTeams && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handlePreviousTeam}
                      disabled={currentTeamIndex === 0}
                      style={{ minWidth: '40px', padding: '8px 12px' }}
                    >
                      &lt;
                    </button>
                  )}

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {coachTeamsList.map((team, index) => (
                      <button
                        key={team.id}
                        type="button"
                        onClick={() => setCurrentTeamIndex(index)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: index === currentTeamIndex ? '2px solid #2e7d32' : '1px solid #ddd',
                          backgroundColor: index === currentTeamIndex ? '#e8f5e9' : validateTeamDefinitions(team.id) ? '#e8f5e9' : '#fff',
                          color: '#333',
                          fontWeight: index === currentTeamIndex ? 'bold' : 'normal',
                          cursor: hasMultipleTeams ? 'pointer' : 'default',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {team.name}
                        {validateTeamDefinitions(team.id) && (
                          <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>
                        )}
                      </button>
                    ))}
                  </div>

                  {hasMultipleTeams && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleNextTeam}
                      disabled={currentTeamIndex === coachTeamsList.length - 1}
                      style={{ minWidth: '40px', padding: '8px 12px' }}
                    >
                      &gt;
                    </button>
                  )}
                </div>
              )}

              {getDefinitionsForCurrentTeam().map((definition) => (
                <div key={definition.definitionKey} className="preseason-definition-card">
                  <div className="season-info-header">
                    <h3 className="preseason-definition-title">{definition.seasonName}</h3>
                    <div className="season-dates-info">
                      <span className="season-date-item">
                        <span className="season-date-label">Początek okresu:</span>
                        <span className="season-date-value">{formatDate(definition.minStartDate)}</span>
                      </span>
                      <span className="season-date-separator">|</span>
                      <span className="season-date-item">
                        <span className="season-date-label">Koniec okresu:</span>
                        <span className="season-date-value">{formatDate(definition.maxEndDate)}</span>
                      </span>
                    </div>
                  </div>

                  <div className="preseason-two-column-layout">
                    {/* Lewa kolumna - podstawowe dane */}
                    <div className="preseason-basic-info">
                      <h4 className="column-header">Podstawowe informacje</h4>
                      <div className="form-group">
                        <label htmlFor={`startDate-${definition.definitionKey}`}>
                          Data rozpoczęcia przygotowań<span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="date"
                          id={`startDate-${definition.definitionKey}`}
                          className="form-input"
                          value={definition.startDate}
                          min={definition.minStartDate}
                          max={definition.endDate || definition.maxEndDate}
                          onChange={(e) => handleDefinitionChange(definition.definitionKey, 'startDate', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`endDate-${definition.definitionKey}`}>
                          Data zakończenia przygotowań<span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="date"
                          id={`endDate-${definition.definitionKey}`}
                          className="form-input"
                          value={definition.endDate}
                          min={definition.startDate || definition.minStartDate}
                          max={definition.maxEndDate}
                          onChange={(e) => handleDefinitionChange(definition.definitionKey, 'endDate', e.target.value)}
                        />
                      </div>

                      {/* Komunikaty błędów walidacji dat */}
                      {dateValidationErrors[definition.definitionKey]?.length > 0 && (
                        <div className="date-validation-errors">
                          {dateValidationErrors[definition.definitionKey].map((error, index) => (
                            <p key={index} className="date-error-message">{error}</p>
                          ))}
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor={`amountOfGames-${definition.definitionKey}`}>
                          Liczba sparingów do rozegrania<span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="number"
                          id={`amountOfGames-${definition.definitionKey}`}
                          className="form-input"
                          min="0"
                          value={definition.amountOfGames}
                          onChange={(e) => handleDefinitionChange(definition.definitionKey, 'amountOfGames', e.target.value)}
                          onBlur={() => handleAmountOfGamesBlur(definition.definitionKey)}
                          placeholder="Wprowadź liczbę"
                        />
                        <button
                          type="button"
                          className="btn btn-fill-weekends"
                          onClick={() => handleFillWithWeekends(definition.definitionKey)}
                          disabled={!definition.startDate || !definition.endDate}
                        >
                          Uzupełnij weekendami
                        </button>
                      </div>

                      {/* Preferencje geograficzne */}
                      <div className="geographic-preferences-section">
                        <h4 className="column-subheader">Preferencje wyszukiwania</h4>

                      </div>
                    </div>

                    {/* Prawa kolumna - sparingi */}
                    <div className="preseason-sparings-info">
                      <h4 className="column-header">Definicje sparingów</h4>
                      {sparingSchedules[definition.definitionKey]?.length > 0 && (
                        <div className="sparings-container">
                          {sparingSchedules[definition.definitionKey].map((sparing, sparingIndex) => (
                            <React.Fragment key={sparingIndex}>
                            <div className="sparing-item-row">
                              <div className="sparing-item-header">
                                <h5 className="sparing-item-title">Sparing {sparingIndex + 1}</h5>
                                <div className="sparing-header-actions">
                                  <button
                                    type="button"
                                    className="sparing-remove-btn"
                                    onClick={() => handleRemoveSparing(definition.definitionKey, sparingIndex)}
                                    title="Usuń sparing"
                                  >
                                    &times;
                                  </button>
                                </div>
                              </div>

                              <div className="sparing-item-fields">
                                <div className="form-group sparing-field">
                                  <label htmlFor={`sparing-startDate-${definition.definitionKey}-${sparingIndex}`}>
                                    Data od<span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    type="date"
                                    id={`sparing-startDate-${definition.definitionKey}-${sparingIndex}`}
                                    className="form-input"
                                    value={sparing.startDate}
                                    onChange={(e) => handleSparingChange(definition.definitionKey, sparingIndex, 'startDate', e.target.value)}
                                  />
                                </div>

                                <div className="form-group sparing-field">
                                  <label htmlFor={`sparing-endDate-${definition.definitionKey}-${sparingIndex}`}>
                                    Data do<span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    type="date"
                                    id={`sparing-endDate-${definition.definitionKey}-${sparingIndex}`}
                                    className="form-input"
                                    value={sparing.endDate}
                                    onChange={(e) => handleSparingChange(definition.definitionKey, sparingIndex, 'endDate', e.target.value)}
                                  />
                                </div>

                                <div className="form-group sparing-field">
                                  <label htmlFor={`sparing-gamesCount-${definition.definitionKey}-${sparingIndex}`}>
                                    Ilość sparingów<span className="required-asterisk">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    id={`sparing-gamesCount-${definition.definitionKey}-${sparingIndex}`}
                                    className="form-input"
                                    min="1"
                                    max="2"
                                    value={sparing.gamesCount || 1}
                                    onChange={(e) => handleSparingChange(definition.definitionKey, sparingIndex, 'gamesCount', e.target.value)}
                                  />
                                </div>

                                <div className="form-group sparing-field sparing-button-field">
                                  <label>&nbsp;</label>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-define-opponent"
                                    onClick={() => setOpponentModalOpen({ preseasonTimeId: definition.definitionKey, sparingIndex })}
                                  >
                                    Określ poziom rozgrywkowy przeciwnika
                                  </button>
                                </div>
                              </div>

                              {/* Badge podsumowania przeciwnika */}
                              {((sparing.opponentTiers && sparing.opponentTiers.length > 0) ||
                                (sparing.opponentYearOfBirths && sparing.opponentYearOfBirths.length > 0) ||
                                (sparing.opponentYouthTiers && sparing.opponentYouthTiers.length > 0)) && (
                                <div className="selection-badges">
                                  {(sparing.opponentTiers || []).map(tierKey => {
                                    const tier = seniorTiers.find(t => t.key === tierKey);
                                    return tier ? (
                                      <span key={tierKey} className="selection-badge badge-opponent">
                                        {tier.displayValue}
                                      </span>
                                    ) : null;
                                  })}
                                  {(sparing.opponentYearOfBirths || []).map(yearKey => {
                                    const year = yearOfBirths.find(y => y.key === yearKey);
                                    return year ? (
                                      <span key={yearKey} className="selection-badge badge-opponent-junior">
                                        {year.displayValue}
                                      </span>
                                    ) : null;
                                  })}
                                  {(sparing.opponentYouthTiers || []).map(tierKey => {
                                    const tier = youthTiers.find(t => t.key === tierKey);
                                    return tier ? (
                                      <span key={tierKey} className="selection-badge badge-opponent-junior">
                                        {tier.displayValue}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              )}


                              {/* Komunikaty błędów walidacji dat sparingu */}
                              {sparingValidationErrors[`${definition.definitionKey}-${sparingIndex}`]?.length > 0 && (
                                <div className="date-validation-errors">
                                  {sparingValidationErrors[`${definition.definitionKey}-${sparingIndex}`].map((error, errorIndex) => (
                                    <p key={errorIndex} className="date-error-message">{error}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                            {sparingIndex < sparingSchedules[definition.definitionKey].length - 1 && (
                              <div className="sparing-insert-btn-wrapper">
                                <button
                                  type="button"
                                  className="sparing-insert-btn"
                                  onClick={() => handleInsertSparing(definition.definitionKey, sparingIndex)}
                                  title="Wstaw sparing"
                                >
                                  +
                                </button>
                              </div>
                            )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      {!sparingSchedules[definition.definitionKey]?.length && (
                        <div className="no-sparings-message">
                          <p>Wprowadź liczbę sparingów aby dodać ich definicje</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Błędy walidacji nad przyciskami */}
              {saveValidationErrors.length > 0 && (
                <div className="save-validation-errors">
                  {saveValidationErrors.map((error, idx) => (
                    <p key={idx} className="save-validation-error">{error}</p>
                  ))}
                </div>
              )}

              {/* Przyciski nawigacji i zapisu */}
              <div className="definition-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                {hasMultipleTeams ? (
                  <>
                    {/* Przycisk "Przejdź do następnej drużyny" */}
                    {!isLastTeam ? (
                      <button
                        className="btn btn-primary btn-save-definitions"
                        onClick={() => {
                          const errors = currentTeam ? getTeamValidationErrors(currentTeam.id) : [];
                          if (errors.length > 0) {
                            setSaveValidationErrors(errors);
                          } else {
                            setSaveValidationErrors([]);
                            handleNextTeam();
                          }
                        }}
                      >
                        Przejdź do następnej drużyny
                      </button>
                    ) : null}

                    {/* Przycisk "Zapisz wszystkie definicje" */}
                    <button
                      className="btn btn-primary btn-save-definitions"
                      onClick={() => {
                        const allErrors = [];
                        coachTeamsList.forEach(team => {
                          const teamErrors = getTeamValidationErrors(team.id);
                          if (teamErrors.length > 0) {
                            allErrors.push(...teamErrors);
                          }
                        });
                        if (allErrors.length > 0) {
                          setSaveValidationErrors(allErrors);
                        } else {
                          setSaveValidationErrors([]);
                          handleSaveDefinitions();
                        }
                      }}
                      disabled={savingDefinitions}
                    >
                      {savingDefinitions ? 'Zapisywanie...' : 'Zapisz wszystkie definicje'}
                    </button>
                  </>
                ) : (
                  /* Pojedyncza drużyna - standardowy przycisk */
                  <button
                    className="btn btn-primary btn-save-definitions"
                    onClick={() => {
                      const errors = getValidationErrors(Object.values(preseasonDefinitions));
                      if (errors.length > 0) {
                        setSaveValidationErrors(errors);
                      } else {
                        setSaveValidationErrors([]);
                        handleSaveDefinitions();
                      }
                    }}
                    disabled={savingDefinitions}
                  >
                    {savingDefinitions ? 'Zapisywanie...' : 'Zapisz definicje'}
                  </button>
                )}
              </div>

            </div>
          )}

          {(isCoach || isSportsClub) && !(isCoach && preseasonTimesToDefine.length > 0) && (
            <div className="settings-section">
              <h2 className="section-title">Okres rozgrywkowy</h2>

              {loading && <p className="loading-text">Ladowanie...</p>}
              {error && <p className="error-text">{error}</p>}

              {!loading && !error && (
                <>
                  <div className="form-group">
                    <label htmlFor="preseasonTime">Wybierz okres rozgrywkowy</label>
                    <select
                      id="preseasonTime"
                      className="form-select"
                      value={selectedPreseasonTime?.id || ''}
                      onChange={handlePreseasonTimeChange}
                    >
                      <option value="">-- Wybierz okres --</option>
                      {preseasonTimes.map((pt) => (
                        <option key={pt.id} value={pt.id}>
                          {pt.name} ({formatDate(pt.minStartDate)} - {formatDate(pt.maxEndDate)})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Wyświetl definicję okresu dla coacha */}
                  {isCoach && selectedPreseasonTime && (
                    <>
                      {coachTeamDefinitionLoading && (
                        <p className="loading-text">Ładowanie ustawień okresu...</p>
                      )}

                      {!coachTeamDefinitionLoading && coachTeamDefinition && (
                        <div className="period-details-layout">
                          <div className="period-description-column">
                            <h3 className="subsection-title">Opis okresu</h3>
                            <div className="preseason-description">
                              {selectedPreseasonTime.type === 'PRESEASON'
                                ? (selectedPreseasonTime.description || 'W tym okresie kalendarze drużyn są uzupełniane o terminy sparingów.')
                                : 'W tym okresie druzyny dodaja informacje o wolnych terminach i przeciwniku z ktorym chcieliby zagrac. Informacje te sa dostepne w wyszukiwarce'}
                            </div>
                          </div>
                          <div className="period-settings-column">
                            <h3 className="subsection-title">Ustawienia okresu dla Twojej drużyny</h3>
                            <div className="date-info-grid">
                              <div className="date-info-item">
                                <div className="date-icon">📅</div>
                                <div className="date-details">
                                  <span className="date-label">Data rozpoczęcia okresu</span>
                                  <span className="date-value">{formatDate(coachTeamDefinition.startDate)}</span>
                                </div>
                              </div>
                              <div className="date-info-item">
                                <div className="date-icon">🏁</div>
                                <div className="date-details">
                                  <span className="date-label">Data zakończenia okresu</span>
                                  <span className="date-value">{formatDate(coachTeamDefinition.endDate)}</span>
                                </div>
                              </div>
                              <div className="date-info-item">
                                <div className="date-icon">⚽</div>
                                <div className="date-details">
                                  <span className="date-label">Liczba sparingów do rozegrania</span>
                                  <span className="date-value">{coachTeamDefinition.amountOfGames ?? '-'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!coachTeamDefinitionLoading && !coachTeamDefinition && (
                        <>
                          <div className="preseason-description" style={{ marginTop: '20px' }}>
                            {selectedPreseasonTime.type === 'PRESEASON'
                              ? (selectedPreseasonTime.description || 'W tym okresie kalendarze drużyn są uzupełniane o terminy sparingów.')
                              : 'W tym okresie druzyny dodaja informacje o wolnych terminach i przeciwniku z ktorym chcieliby zagrac. Informacje te sa dostepne w wyszukiwarce'}
                          </div>
                          <p className="info-text">Nie zdefiniowano jeszcze okresu przygotowawczego dla Twojej drużyny.</p>
                        </>
                      )}
                    </>
                  )}

                  {isSportsClub && selectedPreseasonTime && (
                    <>
                      <div className="form-group">
                        <label htmlFor="teamSelect">Wybierz drużynę</label>
                        <select
                          id="teamSelect"
                          className="form-select"
                          value={selectedTeamId}
                          onChange={handleTeamChange}
                        >
                          <option value="">-- Wybierz drużynę --</option>
                          {teams.map(team => (
                            <option key={team.teamId} value={team.teamId}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {teamDefinitionLoading && (
                        <p className="loading-text">Ładowanie ustawień drużyny...</p>
                      )}

                      {!teamDefinitionLoading && selectedTeamDefinition && (
                        <div className="period-details-layout">
                          <div className="period-description-column">
                            <h3 className="subsection-title">Opis okresu</h3>
                            <div className="preseason-description">
                              {selectedPreseasonTime.type === 'PRESEASON'
                                ? (selectedPreseasonTime.description || 'W tym okresie kalendarze drużyn są uzupełniane o terminy sparingów.')
                                : 'W tym okresie druzyny dodaja informacje o wolnych terminach i przeciwniku z ktorym chcieliby zagrac. Informacje te sa dostepne w wyszukiwarce'}
                            </div>
                          </div>
                          <div className="period-settings-column">
                            <h3 className="subsection-title">Ustawienia okresu dla drużyny</h3>
                            <div className="date-info-grid">
                              <div className="date-info-item">
                                <div className="date-icon">📅</div>
                                <div className="date-details">
                                  <span className="date-label">Data rozpoczęcia okresu</span>
                                  <span className="date-value">{formatDate(selectedTeamDefinition.startDate)}</span>
                                </div>
                              </div>
                              <div className="date-info-item">
                                <div className="date-icon">🏁</div>
                                <div className="date-details">
                                  <span className="date-label">Data zakończenia okresu</span>
                                  <span className="date-value">{formatDate(selectedTeamDefinition.endDate)}</span>
                                </div>
                              </div>
                              <div className="date-info-item">
                                <div className="date-icon">⚽</div>
                                <div className="date-details">
                                  <span className="date-label">Liczba sparingów do rozegrania</span>
                                  <span className="date-value">{selectedTeamDefinition.amountOfGames ?? '-'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!teamDefinitionLoading && !selectedTeamDefinition && (
                        <div className="preseason-description" style={{ marginTop: '20px' }}>
                          {selectedPreseasonTime.type === 'PRESEASON'
                            ? (selectedPreseasonTime.description || 'W tym okresie kalendarze drużyn są uzupełniane o terminy sparingów.')
                            : 'W tym okresie druzyny dodaja informacje o wolnych terminach i przeciwniku z ktorym chcieliby zagrac. Informacje te sa dostepne w wyszukiwarce'}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {!isCoach && !isSportsClub && (
            <div className="settings-section">
              <p className="info-text">Brak dostepnych ustawien dla Twojego typu konta.</p>
            </div>
          )}

          {/* Sekcja powiadomień push */}
          <PushNotificationSettings />
        </div>
      </div>

      {/* Modal definiowania przeciwnika */}
      {opponentModalOpen && (() => {
        const { preseasonTimeId, sparingIndex } = opponentModalOpen;
        const sparing = sparingSchedules[preseasonTimeId]?.[sparingIndex];
        if (!sparing) return null;

        return (
          <div className="modal-overlay" onClick={() => setOpponentModalOpen(null)}>
            <div className="modal-content opponent-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Określ poziom przeciwnika - Sparing {sparingIndex + 1}</h2>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setOpponentModalOpen(null)}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                {/* Sekcja "Z kim chcesz zagrać" */}
                {isCoach && (teamType === 'SENIOR' || teamType === 'YOUTH') && (
                  <div className="form-group" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block', fontSize: '16px' }}>Z kim chcesz zagrać?</label>
                    <div style={{ display: 'flex', gap: '25px' }}>
                      <label className="tier-checkbox-label" style={{ cursor: teamType === 'YOUTH' && isUnder16 ? 'not-allowed' : 'pointer', opacity: teamType === 'YOUTH' && isUnder16 ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          checked={sparingOpponentTypes[`${preseasonTimeId}-${sparingIndex}`]?.senior ?? (teamType === 'YOUTH' ? false : true)}
                          onChange={(e) => handleSparingOpponentTypeChange(preseasonTimeId, sparingIndex, 'senior', e.target.checked)}
                          disabled={teamType === 'YOUTH' && isUnder16}
                        />
                        <span className="tier-checkbox-text">Seniorzy</span>
                      </label>
                      {/* Juniorzy checkbox hidden for seniors-only mode */}
                    </div>
                  </div>
                )}

                {/* Kontener dla sekcji Seniorów i Juniorów */}
                <div className="opponent-sections-container">
                  {/* Sekcja Seniorów */}
                  {(!isCoach ||
                    (teamType === 'SENIOR' && (sparingOpponentTypes[`${preseasonTimeId}-${sparingIndex}`]?.senior ?? true)) ||
                    (teamType === 'YOUTH' && !isUnder16 && (sparingOpponentTypes[`${preseasonTimeId}-${sparingIndex}`]?.senior ?? false))) && (
                    <div className="seniors-filter-section">
                      <label className="section-label">Seniorzy</label>
                      <div className="section-filters-content">
                        <div className="form-group">
                          <label>Poziom rozgrywkowy przeciwnika</label>
                          <div className="tier-checkboxes tier-checkboxes-compact">
                            {seniorTiers
                              .filter(tier => !tier.teamType || tier.teamType === 'SENIOR')
                              .map((tier) => (
                                <label key={tier.key} className="tier-checkbox-label tier-checkbox-label-compact">
                                  <input
                                    type="checkbox"
                                    checked={(sparing.opponentTiers || []).includes(tier.key)}
                                    onChange={(e) => handleTierCheckboxChange(preseasonTimeId, sparingIndex, tier.key, e.target.checked)}
                                  />
                                  <span className="tier-checkbox-text">{tier.displayValue}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sekcja Juniorów - hidden for seniors-only mode */}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setOpponentModalOpen(null)}
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal wyboru województw i powiatów */}
      {territoryModalOpen && (() => {
        const defKey = territoryModalOpen;
        const definition = preseasonDefinitions[defKey];
        if (!definition) return null;
        const selectedVoivodeships = definition.selectedVoivodeships || [];
        const selectedCounties = definition.selectedCounties || [];
        const filteredCounties = getFilteredCounties(selectedVoivodeships);
        // Group counties by voivodeship
        const countiesByVoivodeship = {};
        filteredCounties.forEach(county => {
          if (!countiesByVoivodeship[county.voivodeship]) {
            countiesByVoivodeship[county.voivodeship] = {
              name: county.voivodeshipName,
              counties: []
            };
          }
          countiesByVoivodeship[county.voivodeship].counties.push(county);
        });

        return (
          <div className="modal-overlay" onClick={() => setTerritoryModalOpen(null)}>
            <div className="modal-content territory-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Preferowane województwa i powiaty</h2>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setTerritoryModalOpen(null)}
                >
                  ✕
                </button>
              </div>

              <div className="territory-distance-bar">
                <button
                  type="button"
                  className="btn territory-distance-btn"
                  disabled={!definition.maxSearchDistanceKm || territoryDistanceLoading}
                  onClick={() => handleTerritoryByDistance(defKey)}
                >
                  {territoryDistanceLoading ? 'Obliczanie...' : 'Ustal na podstawie odległości'}
                  {definition.maxSearchDistanceKm && !territoryDistanceLoading && (
                    <span className="territory-distance-badge">{definition.maxSearchDistanceKm} km</span>
                  )}
                </button>
                {!definition.maxSearchDistanceKm && (
                  <span className="territory-distance-hint">Ustaw odległość wyszukiwania aby użyć tej opcji</span>
                )}
              </div>

              <div className="territory-modal-body">
                <div className="territory-voivodeships-panel">
                  <h4 className="territory-panel-header">Województwa</h4>
                  {territoryDictionary.map(voiv => (
                    <label key={voiv.key} className="tier-checkbox-label tier-checkbox-label-compact">
                      <input
                        type="checkbox"
                        checked={selectedVoivodeships.includes(voiv.key)}
                        onChange={() => handleVoivodeshipToggle(defKey, voiv.key)}
                      />
                      <span className="tier-checkbox-text">{voiv.displayValue}</span>
                    </label>
                  ))}
                </div>

                <div className="territory-counties-panel">
                  <h4 className="territory-panel-header">Powiaty (dla zaznaczonych woj.)</h4>
                  {selectedVoivodeships.length === 0 ? (
                    <p className="territory-empty-hint">Zaznacz województwa aby zobaczyć powiaty</p>
                  ) : (
                    Object.entries(countiesByVoivodeship).map(([voivKey, group]) => {
                      const allCountyKeys = group.counties.map(c => c.key);
                      const allSelected = allCountyKeys.length > 0 && allCountyKeys.every(k => selectedCounties.includes(k));
                      const noneSelected = allCountyKeys.every(k => !selectedCounties.includes(k));
                      return (
                        <div key={voivKey} className="territory-county-group">
                          <div className="territory-county-group-header">
                            <span>{group.name}</span>
                            <button
                              type="button"
                              className="btn-select-all-counties"
                              onClick={() => {
                                setPreseasonDefinitions(prev => {
                                  const def = prev[defKey];
                                  const current = def.selectedCounties || [];
                                  let newCounties;
                                  if (allSelected || noneSelected) {
                                    // None selected (whole voiv) or all selected → toggle: select all or deselect all
                                    if (noneSelected) {
                                      newCounties = [...current, ...allCountyKeys];
                                    } else {
                                      newCounties = current.filter(c => !allCountyKeys.includes(c));
                                    }
                                  } else {
                                    // Some selected → select all
                                    newCounties = [...current.filter(c => !allCountyKeys.includes(c)), ...allCountyKeys];
                                  }
                                  return { ...prev, [defKey]: { ...def, selectedCounties: newCounties } };
                                });
                              }}
                            >
                              {allSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                            </button>
                          </div>
                          <div className="territory-checkbox-grid">
                            {group.counties.map(county => (
                              <label key={county.key} className="tier-checkbox-label tier-checkbox-label-compact">
                                <input
                                  type="checkbox"
                                  checked={selectedCounties.includes(county.key)}
                                  onChange={() => handleCountyToggle(defKey, county.key)}
                                />
                                <span className="tier-checkbox-text">{county.displayValue}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="territory-modal-footer">
                <span className="territory-modal-summary">
                  Zaznaczono: {selectedVoivodeships.length} woj.{selectedCounties.length > 0 && `, ${selectedCounties.length} pow.`}
                  {selectedVoivodeships.length === 0 && <span className="territory-footer-hint"> — Pozostaw puste aby szukać wszędzie</span>}
                </span>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setTerritoryModalOpen(null)}
                >
                  Zatwierdź
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <MapPickerModal
        isOpen={campMapPickerOpen !== null}
        onConfirm={handleCampMapConfirm}
        onCancel={() => setCampMapPickerOpen(null)}
        initialLat={campMapPickerOpen ? sparingSchedules[campMapPickerOpen.defKey]?.[campMapPickerOpen.sparingIndex]?.campLatitude : null}
        initialLon={campMapPickerOpen ? sparingSchedules[campMapPickerOpen.defKey]?.[campMapPickerOpen.sparingIndex]?.campLongitude : null}
        initialAddress={campMapPickerOpen ? sparingSchedules[campMapPickerOpen.defKey]?.[campMapPickerOpen.sparingIndex]?.campAddress : ''}
      />
    </div>
  );
}

export default Settings;
